import { useState, useRef, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Text,
  HStack,
  useColorModeValue,
  Box,
  Image,
  IconButton,
  Progress,
  SimpleGrid,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Lightbulb, Wrench, Upload, X, Image as ImageIcon } from 'lucide-react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import { Screenshot } from '../types';
import { useCRMIdeas } from '../useCRMIdeas';
import { useAuth } from '../../../../contexts/AuthContext';

interface CRMAddIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const validationSchema = Yup.object({
  title: Yup.string()
    .required('Title is required')
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must be less than 100 characters'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(2000, 'Description must be less than 2000 characters'),
  type: Yup.string().oneOf(['idea', 'fix']).required('Type is required'),
});

/**
 * CRM Add Idea Modal Component
 * Form for creating new ideas or fixes with screenshot upload capability
 */
export function CRMAddIdeaModal({ isOpen, onClose }: CRMAddIdeaModalProps) {
  const { currentUser } = useAuth();
  const { createIdea } = useCRMIdeas(currentUser?.uid);
  const toast = useToast();
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedScreenshots, setUploadedScreenshots] = useState<Screenshot[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const textColor = useColorModeValue('gray.700', 'gray.300');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const dropzoneBg = useColorModeValue('gray.50', 'gray.700');
  const dropzoneBorder = useColorModeValue('gray.300', 'gray.600');
  const dropzoneHoverBg = useColorModeValue('blue.50', 'gray.600');

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
      setSelectedFiles(prev => [...prev, ...imageFiles]);
    }
  };

  // Remove a selected file
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove an uploaded screenshot
  const removeUploadedScreenshot = (index: number) => {
    setUploadedScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  // Upload files to Firebase Storage
  const uploadFiles = useCallback(async (): Promise<Screenshot[]> => {
    if (selectedFiles.length === 0) return uploadedScreenshots;

    setUploading(true);
    setUploadProgress(0);
    
    const storageRef = firebase.storage().ref();
    const uploadPromises: Promise<Screenshot>[] = [];
    const totalFiles = selectedFiles.length;
    let completedFiles = 0;

    for (const file of selectedFiles) {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `CRM Ideas Screenshots/${currentUser?.uid}/${timestamp}_${safeName}`;
      
      const uploadPromise = storageRef
        .child(filePath)
        .put(file)
        .then(async (snapshot) => {
          const url = await snapshot.ref.getDownloadURL();
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);
          return { url, name: file.name };
        });
      
      uploadPromises.push(uploadPromise);
    }

    try {
      const newScreenshots = await Promise.all(uploadPromises);
      const allScreenshots = [...uploadedScreenshots, ...newScreenshots];
      setUploadedScreenshots(allScreenshots);
      setSelectedFiles([]);
      setUploading(false);
      return allScreenshots;
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploading(false);
      throw error;
    }
  }, [selectedFiles, uploadedScreenshots, currentUser?.uid]);

  // Reset modal state
  const resetModal = () => {
    setSelectedFiles([]);
    setUploadedScreenshots([]);
    setUploadProgress(0);
    setUploading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent borderRadius="xl" boxShadow="xl">
        <Formik
          initialValues={{
            title: '',
            description: '',
            type: 'idea' as 'idea' | 'fix',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              // Upload any pending files first
              const screenshots = await uploadFiles();
              
              // Create the idea with screenshots
              await createIdea({
                ...values,
                screenshots,
                userName: currentUser?.displayName || currentUser?.email || 'Unknown User',
                userEmail: currentUser?.email || '',
              });
              
              toast({
                title: 'Thank you for your feedback!',
                description: 'Your idea has been submitted successfully.',
                status: 'success',
                duration: 5000,
                isClosable: true,
              });
              
              resetForm();
              resetModal();
              onClose();
            } catch (error) {
              console.error('Error submitting idea:', error);
              toast({
                title: 'Error submitting idea',
                description: (error as Error).message,
                status: 'error',
                duration: 5000,
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => {
            const selectedType = values.type;
            
            return (
              <Form>
                <ModalHeader fontWeight="bold" display="flex" alignItems="center" gap={2}>
                  <Icon as={Lightbulb} color="blue.500" />
                  Submit Feedback / Idea
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <VStack spacing={4} align="stretch">
                    <Text color="gray.600" fontSize="sm">
                      Share your ideas for new features, report bugs, or give feedback to help us improve the CRM.
                    </Text>
                    
                    {/* Type Selection */}
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                        Type
                      </FormLabel>
                      <HStack spacing={3}>
                        <Button
                          type="button"
                          flex={1}
                          leftIcon={<Icon as={Lightbulb} w={4} h={4} />}
                          onClick={() => setFieldValue('type', 'idea')}
                          bg={selectedType === 'idea' ? 'blue.100' : bgColor}
                          color={selectedType === 'idea' ? 'blue.700' : textColor}
                          borderWidth={1}
                          borderColor={selectedType === 'idea' ? 'blue.300' : borderColor}
                          _hover={{
                            bg: selectedType === 'idea' ? 'blue.200' : 'gray.50',
                          }}
                          variant={selectedType === 'idea' ? 'solid' : 'outline'}
                        >
                          New Idea
                        </Button>
                        <Button
                          type="button"
                          flex={1}
                          leftIcon={<Icon as={Wrench} w={4} h={4} />}
                          onClick={() => setFieldValue('type', 'fix')}
                          bg={selectedType === 'fix' ? 'orange.100' : bgColor}
                          color={selectedType === 'fix' ? 'orange.700' : textColor}
                          borderWidth={1}
                          borderColor={selectedType === 'fix' ? 'orange.300' : borderColor}
                          _hover={{
                            bg: selectedType === 'fix' ? 'orange.200' : 'gray.50',
                          }}
                          variant={selectedType === 'fix' ? 'solid' : 'outline'}
                        >
                          Needs Fix
                        </Button>
                      </HStack>
                    </FormControl>
                    
                    {/* Title */}
                    <FormControl isRequired isInvalid={!!errors.title && touched.title}>
                      <FormLabel fontSize="sm" fontWeight="medium">
                        Title
                      </FormLabel>
                      <Field as={Input} name="title" placeholder="Brief description of your idea or issue..." />
                      {errors.title && touched.title && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.title}
                        </Text>
                      )}
                    </FormControl>
                    
                    {/* Description */}
                    <FormControl isRequired isInvalid={!!errors.description && touched.description}>
                      <FormLabel fontSize="sm" fontWeight="medium">
                        Details
                      </FormLabel>
                      <Field
                        as={Textarea}
                        name="description"
                        placeholder="Provide more details about your idea or what needs to be fixed..."
                        rows={5}
                        resize="vertical"
                      />
                      {errors.description && touched.description && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.description}
                        </Text>
                      )}
                    </FormControl>
                    
                    {/* Screenshot Upload */}
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium">
                        Screenshots (optional)
                      </FormLabel>
                      
                      {/* Dropzone */}
                      <Box
                        bg={dropzoneBg}
                        border="2px dashed"
                        borderColor={dropzoneBorder}
                        borderRadius="md"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        onClick={() => fileInputRef.current?.click()}
                        _hover={{ borderColor: 'blue.400', bg: dropzoneHoverBg }}
                        transition="all 0.2s"
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          multiple
                          style={{ display: 'none' }}
                        />
                        <VStack spacing={2}>
                          <Icon as={Upload} boxSize={8} color="gray.400" />
                          <Text fontSize="sm" color="gray.500">
                            Click to upload screenshots
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            PNG, JPG, GIF up to 10MB each
                          </Text>
                        </VStack>
                      </Box>
                      
                      {/* Upload Progress */}
                      {uploading && (
                        <Box mt={2}>
                          <Progress value={uploadProgress} size="sm" colorScheme="blue" borderRadius="full" />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Uploading screenshots...
                          </Text>
                        </Box>
                      )}
                      
                      {/* Selected Files Preview */}
                      {selectedFiles.length > 0 && (
                        <Box mt={3}>
                          <Text fontSize="xs" fontWeight="medium" mb={2} color="gray.600">
                            Ready to upload ({selectedFiles.length}):
                          </Text>
                          <SimpleGrid columns={4} spacing={2}>
                            {selectedFiles.map((file, index) => (
                              <Box key={index} position="relative">
                                <Image
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  boxSize="60px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor={borderColor}
                                />
                                <IconButton
                                  aria-label="Remove"
                                  icon={<Icon as={X} boxSize={3} />}
                                  size="xs"
                                  colorScheme="red"
                                  position="absolute"
                                  top={-1}
                                  right={-1}
                                  borderRadius="full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFile(index);
                                  }}
                                />
                              </Box>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )}
                      
                      {/* Uploaded Screenshots Preview */}
                      {uploadedScreenshots.length > 0 && (
                        <Box mt={3}>
                          <Text fontSize="xs" fontWeight="medium" mb={2} color="green.600">
                            Uploaded ({uploadedScreenshots.length}):
                          </Text>
                          <SimpleGrid columns={4} spacing={2}>
                            {uploadedScreenshots.map((screenshot, index) => (
                              <Box key={index} position="relative">
                                <Image
                                  src={screenshot.url}
                                  alt={screenshot.name}
                                  boxSize="60px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  border="2px solid"
                                  borderColor="green.300"
                                />
                                <IconButton
                                  aria-label="Remove"
                                  icon={<Icon as={X} boxSize={3} />}
                                  size="xs"
                                  colorScheme="red"
                                  position="absolute"
                                  top={-1}
                                  right={-1}
                                  borderRadius="full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeUploadedScreenshot(index);
                                  }}
                                />
                              </Box>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )}
                    </FormControl>
                  </VStack>
                </ModalBody>
                <ModalFooter bg="gray.50" borderBottomRadius="xl">
                  <Button variant="ghost" mr={3} onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    colorScheme="blue"
                    isLoading={isSubmitting || uploading}
                    loadingText={uploading ? 'Uploading...' : 'Submitting...'}
                  >
                    Submit Feedback
                  </Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

export default CRMAddIdeaModal;

